import { supabase } from './supabase';
import type { Account } from './supabase';

const mockAccounts: Account[] = [
  {
    id: '1',
    name: 'Main Cash Account',
    bank_name: 'Cash',
    account_number: 'CASH-001',
    address: 'Main Counter',
    opening_balance: 50000,
    current_balance: 75000,
    status: 'active',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: '2',
    name: 'Bank Account - ABC Bank',
    bank_name: 'ABC Bank',
    account_number: '1234567890',
    address: 'Main Branch, City Center',
    opening_balance: 100000,
    current_balance: 150000,
    status: 'active',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
];

const isSupabaseConfigured = () => {
  const url = import.meta.env.VITE_SUPABASE_URL;
  return url && url !== 'https://placeholder.supabase.co';
};

export const bankAccountsService = {
  async getAll(): Promise<Account[]> {
    if (!isSupabaseConfigured()) {
      return mockAccounts;
    }

    try {
      const { data, error } = await supabase
        .from('accounts')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching accounts:', error);
      return mockAccounts;
    }
  },

  async getById(id: string): Promise<Account | null> {
    if (!isSupabaseConfigured()) {
      return mockAccounts.find(a => a.id === id) || null;
    }

    try {
      const { data, error } = await supabase
        .from('accounts')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching account:', error);
      return mockAccounts.find(a => a.id === id) || null;
    }
  },

  async create(account: Omit<Account, 'id' | 'created_at' | 'updated_at'>): Promise<Account> {
    if (!isSupabaseConfigured()) {
      const newAccount: Account = {
        ...account,
        id: Math.random().toString(36).substr(2, 9),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      mockAccounts.unshift(newAccount);
      return newAccount;
    }

    try {
      const { data, error } = await supabase
        .from('accounts')
        .insert([account])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating account:', error);
      throw error;
    }
  },

  async update(id: string, updates: Partial<Account>): Promise<Account> {
    if (!isSupabaseConfigured()) {
      const index = mockAccounts.findIndex(a => a.id === id);
      if (index !== -1) {
        mockAccounts[index] = {
          ...mockAccounts[index],
          ...updates,
          updated_at: new Date().toISOString(),
        };
        return mockAccounts[index];
      }
      throw new Error('Account not found');
    }

    try {
      const { data, error } = await supabase
        .from('accounts')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating account:', error);
      throw error;
    }
  },

  async delete(id: string): Promise<void> {
    if (!isSupabaseConfigured()) {
      const index = mockAccounts.findIndex(a => a.id === id);
      if (index !== -1) {
        mockAccounts.splice(index, 1);
      }
      return;
    }

    try {
      const { error } = await supabase
        .from('accounts')
        .delete()
        .eq('id', id);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting account:', error);
      throw error;
    }
  },
};
